import {
  inferVisiblityOptionFromPriceSettings,
  updatePriceSettingsFromVisibilityOption,
} from '../../utils/pricing';
import { errorDescriptionFromResponse } from '../../utils/error';
import { FindTimeZone, JstzMatchesTzInfo, JstzToTzInfo } from '../../utils/timezone';

export default class EditEventController {
  constructor(
    $scope,
    $state,
    $uibModalInstance,
    $q,
    $uibModal,
    Broadcast,
    Facebook,
    ConfirmDialog,
    EditOrCreateEventService,
    OneOrAllDialog,
    toaster,
    broadcastId,
    activeTab,
    $rootScope
  ) {
    'ngInject';
    this.$scope = $scope;
    this.$state = $state;
    this.$uibModal = $uibModal;
    this.$uibModalInstance = $uibModalInstance;
    this.Broadcast = Broadcast;
    this.ConfirmDialog = ConfirmDialog;
    this.OneOrAllDialog = OneOrAllDialog;
    this.toaster = toaster;

    this.activeTab = activeTab;

    this.fb = Facebook;
    this.fb.init();

    this.service = EditOrCreateEventService.getInstance();
    this.service.initBroadcast(broadcastId);
    this.service.initScope($scope);

    this.showError = false;

    const accountTimezone__tzInfo = $rootScope.account.time_zone;
    const currentTimezone__jstz = FindTimeZone();
    this.currentTimezoneValue = currentTimezone__jstz;
    this.currentTimezone = JstzToTzInfo(currentTimezone__jstz);
    this.timezoneMatchesAccount = JstzMatchesTzInfo(currentTimezone__jstz, accountTimezone__tzInfo);

    this.minStartTime = moment().startOf('day').toDate();

    if (this.service.broadcast.$promise) {
      this.service.broadcast.$promise.then(() => {
        this.showSocial = this.service.broadcast.simulcast_configurations.length > 0;
        this.fb.init();

        // Derived data for editing
        this.isImported = this.service.broadcast.stream_source == 'imported';
        this.canEditStart = this.service.broadcast.timeframe == 'future' || this.isImported;
        this.canEditStop = this.service.broadcast.timeframe != 'past' || this.isImported;
        if (this.isImported) {
          this.minStartTime = moment(this.service.broadcast.starts_at).startOf('day').toDate();
        }

        // Podcast settings expanded only if already updated
        const cs = this.service.broadcast.content_settings;
        this.showPodcast = cs.podcast_preview || cs.podcast_subtitle || cs.podcast_summary;
      });
    } else {
      this.fb.init();
    }

    this.loadingPromises = [
      this.service.broadcast.$promise,
      this.service.boxcasters.$promise,
      this.service.account.$promise,
      this.service.channels.$promise,
    ];

    $q.all(this.loadingPromises).catch((e) => {
      console.error(e);
      toaster.pop(
        'warning',
        'Error loading broadcast',
        'Sorry, we were not able to load the broadcast. Please refresh this page and try again.'
      );
      this.cancel();
    });

    this.confirmOnExit = false;
    $scope.$watch(
      () => $scope.form.$pristine,
      (isPristine) => {
        this.confirmOnExit = this.confirmOnExit || !isPristine;
      }
    );
  }

  loginFacebook() {
    this.fb.login().then(() => {
      this.service.initializeEmptyFacebookAccountIDs();
    });
  }

  logoutFacebook() {
    this.fb.logout().then(() => this.fb.init());
  }

  facebookLiveSimulcastFeedType(config) {
    return config.fb_feed_id.split(':', 1)[0];
  }

  facebookLiveSimulcastFeedID(config) {
    return config.fb_feed_id.replace(/^[^:]*:/, '');
  }

  fbLiveShowGlass(config) {
    return (
      config.fb_feed_id &&
      config.facebook_account_id &&
      config.facebook_account_id != this.fb.facebook_account_id
    );
  }

  cancel() {
    let doIt = true;
    if (this.confirmOnExit) {
      doIt = confirm('Are you sure you want to abandon your changes?');
    }
    if (doIt) {
      this.$uibModalInstance.dismiss('cancel');
    }
  }

  clone() {
    this.cancel();
    //route to vue - that will handle if it should route back to angular or not.
    window.location.href = `${window.VUE_DASHBOARD_ROOT}/schedule/broadcast?cloneId=${this.service.broadcast.id}`;
  }

  delete() {
    const { Broadcast, ConfirmDialog, OneOrAllDialog, toaster, $state, $uibModalInstance } = this;

    const deleteThisBroadcast = () => {
      const resource = Broadcast.delete(
        { id: this.service.broadcast.id },
        () => {
          $uibModalInstance.dismiss();
          this.cancel();
          $state.go('home', {}, { reload: true });
        },
        (e) => {
          if (
            e.status === 409 &&
            e.data &&
            e.data.error_description &&
            RegExp('uses? the recording of this broadcast').test(e.data.error_description)
          ) {
            toaster.pop('warning', 'Error deleting broadcast', e.data.error_description + '.');
          } else {
            alert('Error deleting broadcast. Please reload this page and try again.');
          }
        }
      );
      this.loadingPromises = [resource.$promise];
    };

    const deleteBroadcastWithRecurrences = () => {
      const resource = Broadcast.deleteRecurring(
        { id: this.service.broadcast.id },
        {
          id: this.service.broadcast.id,
          recurrence: {
            id: this.service.broadcast.recurrence.id,
            broadcasts: this.service.broadcast.recurrence.broadcasts,
          },
        },
        () => {
          $uibModalInstance.dismiss();
          this.cancel();
          $state.go('home', {}, { reload: true });
        },
        (e) => {
          if (
            e.status === 409 &&
            e.data &&
            e.data.error_description &&
            RegExp('scheduled to use the recording of this broadcast').test(
              e.data.error_description
            )
          ) {
            toaster.pop(
              'warning',
              'Error deleting broadcast',
              e.data.error_description.replace(
                'of this broadcast',
                'of one or more of these broadcasts'
              ) + '.'
            );
          } else {
            alert('Error deleting broadcasts. Please reload this page and try again.');
          }
        }
      );
      this.loadingPromises = [resource.$promise];
    };

    const promptAndDelete = (deleteFn) => {
      const modalOptions = {
        closeButtonText: 'Cancel',
        actionButtonText: 'Delete Broadcast',
        actionButtonClass: 'danger',
        headerText: 'Please Confirm',
        bodyText:
          'Are you sure you want to permanently delete this broadcast? Once deleted, a broadcast cannot be recovered.',
      };
      if (deleteFn === deleteBroadcastWithRecurrences) {
        modalOptions.actionButtonText = 'Delete Broadcasts';
        modalOptions.bodyText =
          'Are you sure you want to permanently delete these broadcasts? Once deleted, broadcasts cannot be recovered.';
      }
      ConfirmDialog.show(modalOptions).then(deleteFn);
    };

    if (
      this.service.broadcast.timeframe != 'past' &&
      this.service.broadcast.recurrence &&
      this.service.broadcast.recurrence.broadcasts &&
      this.service.broadcast.recurrence.broadcasts.length
    ) {
      let otherBroadcasts = [];
      let otherBroadcastsText = '';
      if (this.service.broadcast.timeframe == 'future') {
        // If this broadcast is in the future, then "other broadcasts" is only other future broadcasts
        otherBroadcasts = _.filter(
          this.service.broadcast.recurrence.broadcasts,
          (b) => b.timeframe == 'future'
        );
        otherBroadcastsText = 'future';
      } else {
        // If this broadcast is pre-roll or current, then "other broadcasts" is current AND future broadcasts
        otherBroadcasts = _.filter(
          this.service.broadcast.recurrence.broadcasts,
          (b) => b.timeframe != 'past'
        );
        otherBroadcastsText = 'current and future';
      }

      if (otherBroadcasts.length) {
        // Other non-past recurring broadcasts in the series!
        const modalOptions = {
          headerText: `Delete all ${otherBroadcastsText} broadcasts in the series?`,
          bodyText: `This broadcast is one of ${
            otherBroadcasts.length + 1
          } ${otherBroadcastsText} broadcasts in a recurring series. Do you want to delete this broadcast only, or all ${otherBroadcastsText} broadcasts in the series?`,
          allButtonClass: 'danger',
          oneButtonClass: 'danger',
        };
        OneOrAllDialog.show(modalOptions).then((result) => {
          if (result == 'one') {
            // Just delete this one
            promptAndDelete(deleteThisBroadcast);
          } else {
            // Delete all the future ones
            this.service.broadcast.recurrence.broadcasts = otherBroadcasts;
            promptAndDelete(deleteBroadcastWithRecurrences);
          }
        });
      } else {
        // All other recurring broadcasts in the series are past, so we're just operating on this one.
        promptAndDelete(deleteThisBroadcast);
      }
    } else {
      // This is a past broadcast or single non-recurring broadcast
      promptAndDelete(deleteThisBroadcast);
    }
  }

  addSimulcastDestination(d, params) {
    this.confirmOnExit = true;
    return this.service.addSimulcastDestination(d, params);
  }

  removeSimulcastDestination(d) {
    this.confirmOnExit = true;
    return this.service.removeSimulcastDestination(d);
  }

  get styleDoNotRecord() {
    if (this.service.broadcast.timeframe == 'future') {
      return { cursor: 'pointer' };
    } else {
      return {
        cursor: 'not-allowed',
        color: '#aaa',
      };
    }
  }

  toggleDoNotRecord() {
    // Don't toggle unless it's in the future
    if (this.service.broadcast.timeframe != 'future') return;

    this.service.broadcast.do_not_record = !this.service.broadcast.do_not_record;
  }

  submit() {
    const { $uibModal, OneOrAllDialog } = this;

    const saveThisBroadcast = () => {
      this.showError = false;
      this.submitting = true;

      this.service
        .saveChanges()
        .then((broadcast) => {
          this.submitting = false;
          this.$uibModalInstance.close(broadcast);
        })
        .catch((error) => {
          let errorDetails = errorDescriptionFromResponse(error);
          if (errorDetails == 'invalid') {
            this.showError = true;
          } else {
            alert(
              'Unable to save broadcast. Please check your fields and try again.\n\n' + errorDetails
            );
          }
          this.submitting = false;
        });
    };

    const checkForRecurringUpdates = () => {
      if (
        this.service.broadcast.timeframe != 'past' &&
        this.service.broadcast.recurrence &&
        this.service.broadcast.recurrence.broadcasts &&
        this.service.broadcast.recurrence.broadcasts.length
      ) {
        let otherBroadcasts = [];
        let otherBroadcastsText = '';
        if (this.service.broadcast.timeframe == 'future') {
          // If this broadcast is in the future, then "other broadcasts" is only other future broadcasts
          otherBroadcasts = _.filter(
            this.service.broadcast.recurrence.broadcasts,
            (b) => b.timeframe == 'future'
          );
          otherBroadcastsText = 'future';
        } else {
          // If this broadcast is pre-roll or current, then "other broadcasts" is current AND future broadcasts
          otherBroadcasts = _.filter(
            this.service.broadcast.recurrence.broadcasts,
            (b) => b.timeframe != 'past'
          );
          otherBroadcastsText = 'current and future';
        }

        if (otherBroadcasts.length) {
          // Other recurring broadcasts in the series!
          const modalOptions = {
            headerText: `Update all ${otherBroadcastsText} broadcasts in the series?`,
            bodyText: `This broadcast is one of ${
              otherBroadcasts.length + 1
            } ${otherBroadcastsText} broadcasts in a recurring series. Do you want to apply your edits to this broadcast only, or all ${otherBroadcastsText} broadcasts in the series?`,
          };
          OneOrAllDialog.show(modalOptions).then((result) => {
            if (result == 'one') {
              // Just update this one
              delete this.service.broadcast.recurrence;
              saveThisBroadcast();
            } else {
              // Update all the future ones
              this.service.broadcast.recurrence.broadcasts = otherBroadcasts;
              saveThisBroadcast();
            }
          });
        } else {
          // All other recurring broadcasts in the series are past, so we're just operating on this one.
          delete this.service.broadcast.recurrence;
          saveThisBroadcast();
        }
      } else {
        // This is a past broadcast or single non-recurring broadcast
        delete this.service.broadcast.recurrence;
        saveThisBroadcast();
      }
    };

    if (this.service.broadcast.timeframe != 'past' && this.service.conflictingEvents.length > 0) {
      const { conflictingEvents } = this.service;
      $uibModal
        .open({
          template: require('./modal-conflicting-events.html'),
          controller: function ($scope, $uibModalInstance) {
            'ngInject';
            $scope.conflictingEvents = conflictingEvents;
            $scope.ok = (result) => $uibModalInstance.close(result);
            $scope.close = () => $uibModalInstance.dismiss('cancel');
          },
        })
        .result.then(checkForRecurringUpdates);
    } else {
      checkForRecurringUpdates();
    }
  }
}



// WEBPACK FOOTER //
// ./app/pages/events/edit.js